import React, {FC} from 'react';
import styled from 'styled-components';

import {getIsUserGlobalAdmin} from '../../../../libs/shared/core/src/store/data/access/commonAccessSelectors';
import {getHasAdminSetupGuide} from '../../../../libs/shared/core/src/store/featureFlags/featureFlagsSelectors';
import {AppPanelId} from '../../../../libs/shared/core/src/store/panels/panelStateBase';
import {mainButtonHorizontalPadding} from '../../../../libs/shared/ui/src/components/buttons/buttonContent';
import {VisualSizesEnum} from '../../../../libs/shared/ui/src/styles/commonStyles';
import {useAppSelector} from '../../../helpers/appReduxHelpers';
import {StartGuideTaskTypesEnum} from '../../../models/api/preferencesModel';
import {
  getHasSetupGuide,
  getIsInTrialOrManagedTrial,
  getIsNotificationCenterEnabled,
} from '../../../store/data/company/companySelectors';
import {
  getCompletedStartGuideTasks,
  getWasOnboardingCompleted,
} from '../../../store/data/preferences/preferencesSelectors';
import {getIsAdminSetupGuideAvailable} from '../../../store/stateless/startGuideSelectors';
import {sidePanelSwitcherWidth} from '../../inboxes/side/switcher/sidePanelSwitcherHelpers';
import {Help} from './help/help';
import {Incidents} from './incidents/incidents';
import {NotificationsCenter} from './notificationsCenter/notificationsCenter';
import {AdminSetupGuideButton} from './onboarding/adminSetupGuideButton';
import {Onboarding} from './onboarding/onboarding';
import {SettingsAppButton} from './settingsAppButton';
import {topBarButtonPadding, topBarButtonSize} from './topBarButton';
import {topBarButtonGap, topBarHorizontalPadding} from './topBarHelpers';
import {UserProfile} from './userProfile/userProfile';
import {userProfileButtonSize} from './userProfile/userProfileButton';

/*
 * Props.
 */

interface SettingsSectionProps {
  panelId: AppPanelId;
  isSettingsAppSelected: boolean;
}

/*
 * Styles.
 */

const StyledSettingsSectionWrapper = styled.div`
  -webkit-app-region: drag;

  flex: auto;

  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: ${topBarButtonGap}px;
`;

const StyledAdminSetupGuideButton = styled(AdminSetupGuideButton)`
  /* Match the visual distance to the neighboring top bar button icon by compensating for our bigger padding. */
  margin-right: ${topBarButtonPadding - mainButtonHorizontalPadding[VisualSizesEnum.MEDIUM]}px;
`;

const StyledUserProfile = styled(UserProfile)`
  /* Match the height of other top bar buttons so tooltips and dropdowns are positioned the same. */
  height: ${topBarButtonSize}px;

  /* Match the visual distance to the neighboring top bar button icon. */
  margin-left: ${topBarButtonPadding}px;

  /* Horizontally align the user profile avatar with the side panel switch (P4) icons. */
  margin-right: ${(sidePanelSwitcherWidth - userProfileButtonSize) / 2 - topBarHorizontalPadding}px;
`;

/*
 * Component.
 */

export const SettingsSection: FC<SettingsSectionProps> = ({panelId, isSettingsAppSelected}) => {
  const shouldHideIncidents = useAppSelector(getIsInTrialOrManagedTrial);
  const isNotificationCenterEnabled = useAppSelector(getIsNotificationCenterEnabled);
  const isGlobalAdmin = useAppSelector(getIsUserGlobalAdmin);
  const hasAdminSetupGuide = useAppSelector(getHasAdminSetupGuide);
  const isAdminSetupGuideAvailable = useAppSelector(getIsAdminSetupGuideAvailable);

  const completedStartGuideTasks = useAppSelector(getCompletedStartGuideTasks);
  const hasCompletedAllTasks = completedStartGuideTasks.includes(
    StartGuideTaskTypesEnum.HELP_CENTER_SUCCESS_DISMISS,
  );

  const isSetupGuideEnabled = useAppSelector(getHasSetupGuide);
  const wasOnboardingCompleted = useAppSelector(getWasOnboardingCompleted);
  const hasP0SetupGuide = isSetupGuideEnabled && isGlobalAdmin && !hasCompletedAllTasks;
  // Only show the onboarding setup guide button in p0 after the questionnaire is completed.
  const shouldRenderOnboarding = hasP0SetupGuide && wasOnboardingCompleted && !hasAdminSetupGuide;

  return (
    <StyledSettingsSectionWrapper>
      {isAdminSetupGuideAvailable && <StyledAdminSetupGuideButton panelId={panelId} />}
      {shouldRenderOnboarding && <Onboarding panelId={panelId} />}
      {!shouldHideIncidents && <Incidents />}
      <Help panelId={panelId} />
      <SettingsAppButton panelId={panelId} isSelected={isSettingsAppSelected} />
      {isNotificationCenterEnabled && <NotificationsCenter />}
      <StyledUserProfile />
    </StyledSettingsSectionWrapper>
  );
};
